import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import { FilterChip, FilterRow } from "@/components/admin/filter-chip"
import { PageHeader } from "@/components/console/page-header"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { PaginationCardMinimal } from "@/components/ui/pagination"
import { Table, TableCard } from "@/components/ui/table"
import { AdminLayout } from "@/layouts/admin-layout"
import { adminEmailMessagePath, adminEmailMessagesPath } from "@/routes"

import {
  type EmailMessageSummary,
  type EmailStatus,
  ROUTE_COLOR,
  STATUS_COLOR,
  STATUS_LABEL,
} from "./types"

interface Filters {
  recipient: string | null
  status: string | null
  mailer: string | null
  sent_on: string | null
}

interface Props {
  messages: EmailMessageSummary[]
  filters: Filters
  available_filters: { statuses: EmailStatus[]; mailers: string[] }
  pagination: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
}

export default function AdminEmailMessagesIndex({
  messages,
  filters,
  available_filters,
  pagination,
}: Props) {
  const [recipient, setRecipient] = useState(filters.recipient ?? "")

  const visitWith = (next: Partial<Filters & { page: number }>) => {
    const merged = { ...filters, recipient: recipient || null, ...next }
    const data: Record<string, string | number> = {}
    if (merged.recipient) data.recipient = merged.recipient
    if (merged.status) data.status = merged.status
    if (merged.mailer) data.mailer = merged.mailer
    if (merged.sent_on) data.sent_on = merged.sent_on
    if (next.page && next.page > 1) data.page = next.page
    router.visit(adminEmailMessagesPath(), {
      data,
      preserveState: true,
      preserveScroll: true,
      only: ["messages", "filters", "pagination"],
    })
  }

  const clearFilters = () => {
    setRecipient("")
    visitWith({ recipient: null, status: null, mailer: null, sent_on: null })
  }

  const hasActiveFilters = !!(
    filters.recipient ||
    filters.status ||
    filters.mailer ||
    filters.sent_on
  )

  const tokens = [
    filters.recipient && {
      label: `recipient: ${filters.recipient.toLowerCase()}`,
      onClear: () => {
        setRecipient("")
        visitWith({ recipient: null })
      },
    },
    filters.status && {
      label:
        `status: ${STATUS_LABEL[filters.status as EmailStatus] ?? filters.status}`.toLowerCase(),
      onClear: () => visitWith({ status: null }),
    },
    filters.mailer && {
      label: `mailer: ${filters.mailer.toLowerCase()}`,
      onClear: () => visitWith({ mailer: null }),
    },
    filters.sent_on && {
      label: `sent on: ${filters.sent_on}`,
      onClear: () => visitWith({ sent_on: null }),
    },
  ].filter((token): token is { label: string; onClear: () => void } =>
    Boolean(token),
  )

  return (
    <AdminLayout>
      <Head title="Email Ledger" />
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          crumbs={[{ label: "email" }, { label: "ledger" }]}
          title="Email ledger"
          description="Every send, with its cause attached."
        />

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <input
              type="date"
              aria-label="Sent on"
              value={filters.sent_on ?? ""}
              onChange={(e) => visitWith({ sent_on: e.target.value || null })}
              className="border-primary text-secondary rounded-lg border bg-transparent px-3 py-1.5 text-sm"
            />
          </div>
          <FilterRow label="Status">
            <FilterChip
              active={!filters.status}
              onClick={() => visitWith({ status: null })}
            >
              All
            </FilterChip>
            {available_filters.statuses.map((status) => (
              <FilterChip
                key={status}
                active={filters.status === status}
                onClick={() => visitWith({ status })}
              >
                {STATUS_LABEL[status]}
              </FilterChip>
            ))}
          </FilterRow>
          {available_filters.mailers.length > 0 && (
            <FilterRow label="Mailer">
              <FilterChip
                active={!filters.mailer}
                onClick={() => visitWith({ mailer: null })}
              >
                All
              </FilterChip>
              {available_filters.mailers.map((mailer) => (
                <FilterChip
                  key={mailer}
                  active={filters.mailer === mailer}
                  onClick={() => visitWith({ mailer })}
                >
                  {mailer}
                </FilterChip>
              ))}
            </FilterRow>
          )}
        </div>

        <TableCard.Root bleed>
          <TableCard.Caption
            title="Ledger"
            count={`${pagination.total} ${pagination.total === 1 ? "message" : "messages"}`}
            tokens={tokens}
            note="created ↓"
            trailing={
              <TableCard.Filter
                value={recipient}
                onChange={setRecipient}
                onCommit={(v) => {
                  if ((filters.recipient ?? "") !== v)
                    visitWith({ recipient: v || null })
                }}
                placeholder="filter · recipient"
                label="Recipient"
              />
            }
          />
          {messages.length === 0 ? (
            <TableCard.Empty
              action={
                hasActiveFilters && (
                  <Button size="md" color="secondary" onPress={clearFilters}>
                    Clear filters
                  </Button>
                )
              }
            >
              No messages match these filters. Try clearing one of the filters
              above.
            </TableCard.Empty>
          ) : (
            <>
              <Table aria-label="Email messages" rows="two-line">
                <Table.Header>
                  <Table.Head
                    id="recipient"
                    isRowHeader
                    label="Recipient"
                    className="w-full"
                  />
                  <Table.Head id="mailer" label="Mailer" />
                  <Table.Head id="cause" label="Cause" />
                  <Table.Head id="status" label="Status" />
                  <Table.Head id="route" label="Route" />
                  <Table.Head
                    id="last_event"
                    label="Last event"
                    align="right"
                  />
                </Table.Header>
                <Table.Body items={messages}>
                  {(message) => (
                    <Table.Row
                      id={message.id}
                      href={adminEmailMessagePath(message.id)}
                    >
                      <Table.Lead sub={message.subject ?? "—"}>
                        {message.to}
                      </Table.Lead>
                      <Table.Cell>
                        <Badge type="modern" color="gray" size="sm">
                          {message.mailer}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>{message.cause ?? "—"}</Table.Cell>
                      <Table.Status tier={STATUS_COLOR[message.status]}>
                        {STATUS_LABEL[message.status]}
                      </Table.Status>
                      <Table.Status
                        tier={ROUTE_COLOR[message.route] ?? "neutral"}
                      >
                        {message.route}
                      </Table.Status>
                      <Table.When at={message.last_event_at} relative />
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
              <TableCard.Footer
                shown={messages.length}
                total={pagination.total}
                filters
                trailing={
                  pagination.total_pages > 1 && (
                    <PaginationCardMinimal
                      align="right"
                      page={pagination.page}
                      total={pagination.total_pages}
                      onPageChange={(page) => visitWith({ page })}
                      className="border-0 p-0 md:p-0"
                    />
                  )
                }
              />
            </>
          )}
        </TableCard.Root>
      </div>
    </AdminLayout>
  )
}
